import { Box, MenuItem } from "@mui/material";
import { saveAs } from "file-saver";
import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { reactSelectSMStyle, styles } from "../../../Utils/Common";
import {
  StyledDropButton,
  StyledDropMenu,
} from "../../../Utils/Style/Dropdownstyle";
import { SortingTypeI } from "../../../Utils/consts";
import { printBarcodeRecords, printSupplyPrinterContent } from "./barcodePrint";
import {
  extractBarcodeRecords,
  initialSearchQuery,
  isShippableStatus,
  mergeBarcodeRecords,
  queryDisplayTagNames,
  sortById,
} from "./helpers";
import {
  oncologySupplyQueryKeys,
  useOncologyOrderActionLookup,
  useOncologyOrders,
  useOncologyPrinters,
} from "./hooks";
import OrderListTable, { ListMode } from "./OrderListTable";
import {
  BarcodeRecord,
  LookupOption,
  OrderAction,
  SupplyOrder,
  SupplyOrderItem,
} from "./types";
import usePagination from "Shared/hooks/usePagination";
import useLang from "Shared/hooks/useLanguage";
import ArrowBottomIcon from "Shared/SVG/ArrowBottomIcon";
import CustomPagination from "Shared/JsxPagination";
import SupplyManagmentService from "Services/SupplyManagement/SupplyManagmentService";
import PermissionComponent from "Shared/Common/Permissions/PermissionComponent";

const shippingCourier = [
  { value: "UPS", label: "UPS" },
  { value: "FedEx", label: "FedEx" },
];

const statusByMode: Record<ListMode, string> = {
  new: "NewOrders",
  shipped: "shipped",
  rejected: "rejected",
};

const normalizeSearchRequest = (searchRequest: typeof initialSearchQuery) =>
  Object.fromEntries(
    Object.entries(searchRequest).map(([key, value]) => [
      key,
      typeof value === "string" ? value.trim() : value,
    ])
  );

const buildQueryModel = (
  mode: ListMode,
  searchRequest: typeof initialSearchQuery
) => {
  const queryModel = normalizeSearchRequest(searchRequest);

  if (mode === "new") {
    delete (queryModel as any).status;
    return queryModel;
  }

  return {
    ...queryModel,
    status: statusByMode[mode],
  };
};

const getInitialSearchState = (mode: ListMode) => ({
  ...initialSearchQuery,
  status: mode === "new" ? "" : statusByMode[mode],
});

type SupplyPrinterContentPayload = {
  printerId: number;
  supplyOrderId: number | null;
  supplyItemId?: number | null;
  kitName: string;
  barcode: string;
};

type SupplyPrinterContentVariables = {
  payload: SupplyPrinterContentPayload;
  printer: LookupOption;
};

function OrderList({ mode }: { mode: ListMode }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showShipModal, setShowShipModal] = useState(false);
  const [searchRequest, setSearchRequest] = useState(() =>
    getInitialSearchState(mode)
  );
  const [appliedSearch, setAppliedSearch] = useState(() =>
    getInitialSearchState(mode)
  );
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [sort, setSorting] = useState<SortingTypeI>(sortById);
  const [shippingNow, setShippingNow] = useState({
    courierName: "",
    trackingNumber: "",
  });
  const [exportAnchorEl, setExportAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
  const [pendingBarcodes, setPendingBarcodes] = useState<
    Record<number, BarcodeRecord[]>
  >({});
  const [selectedPrinter, setSelectedPrinter] = useState<LookupOption | null>(
    null
  );
  const [actionRowId, setActionRowId] = useState<number | null>(null);
  const [printBarcodeRowId, setPrintBarcodeRowId] = useState<number | null>(
    null
  );

  const {
    curPage,
    pageSize,
    total,
    totalPages,
    pageNumbers,
    nextPage,
    prevPage,
    showPage,
    setPageSize,
    setTotal,
    setCurPage,
  } = usePagination();

  const { data: actionLookup = [] } = useOncologyOrderActionLookup();
  const { data: printers = [] } = useOncologyPrinters();

  const ordersPayload = useMemo(
    () => ({
      pageNumber: curPage,
      pageSize,
      queryModel: buildQueryModel(mode, appliedSearch),
      sortColumn: sort.clickedIconData,
      sortDirection: sort.sortingOrder,
    }),
    [appliedSearch, curPage, mode, pageSize, sort]
  );

  const {
    data: orderResult,
    isLoading: ordersLoading,
    isFetching: ordersFetching,
    refetch: refetchOrders,
  } = useOncologyOrders(ordersPayload);

  const rows = orderResult?.rows ?? [];
  const loading = ordersLoading || (ordersFetching && rows.length === 0);
  const selectAll = rows.length > 0 && selectedIds.length === rows.length;

  const selectedRows = useMemo(
    () => rows.filter((row) => selectedIds.includes(row.id)),
    [rows, selectedIds]
  );

  const canShipSelection =
    selectedRows.length > 0 &&
    selectedRows.every((row) => isShippableStatus(row));

  useEffect(() => {
    setTotal(orderResult?.total ?? 0);
  }, [orderResult?.total, setTotal]);

  useEffect(() => {
    setSelectedIds((prev) =>
      prev.filter((id) => rows.some((row) => row.id === id))
    );
  }, [rows]);

  useEffect(() => {
    setSearchRequest(getInitialSearchState(mode));
    setAppliedSearch(getInitialSearchState(mode));
    setSelectedIds([]);
    setSorting(sortById);
    setExpandedOrderId(null);
    setPendingBarcodes({});
    setCurPage(1);
  }, [mode, setCurPage]);

  useEffect(() => {
    if (!selectedPrinter && printers.length > 0) {
      setSelectedPrinter(
        printers.find((printer) => printer.isDefault) ?? printers[0]
      );
    }
  }, [printers, selectedPrinter]);

  useEffect(() => {
    setCurPage(1);
  }, [pageSize, setCurPage]);

  const exportMutation = useMutation(
    (selectedOnly: boolean) =>
      SupplyManagmentService.exportOrdersToExcel({
        ...(mode !== "new" ? { status: statusByMode[mode] } : {}),
        selectedRows: selectedOnly ? selectedIds : [],
        queryModel: buildQueryModel(mode, appliedSearch),
      }),
    {
      onSuccess: (res: any, selectedOnly) => {
        if (res?.data?.result?.httpStatusCode === 200) {
          toast.success(res?.data?.message);
          base64ToExcel(
            res.data?.result?.data.fileContents,
            mode === "new" ? "Oncology New Orders" : `Oncology ${mode}`
          );
          if (selectedOnly) {
            setSelectedIds([]);
          }
          return;
        }

        toast.error(res?.data?.message || t("Unable to export records"));
      },
      onError: (error: any) => {
        console.error(error);
        toast.error(
          error?.response?.data?.message || t("Unable to export records")
        );
      },
    }
  );

  const shipMutation = useMutation(SupplyManagmentService.saveShippingDetails, {
    onSuccess: (res: any) => {
      if (res?.data?.httpStatusCode === 200) {
        toast.success(res?.data?.message);
        setShowShipModal(false);
        setShippingNow({ courierName: "", trackingNumber: "" });
        setSelectedIds([]);
        queryClient.invalidateQueries(oncologySupplyQueryKeys.ordersRoot());
        return;
      }

      toast.error(res?.data?.message || t("Unable to ship orders"));
    },
    onError: (error: any) => {
      console.error(error);
      toast.error(error?.response?.data?.message || t("Unable to ship orders"));
    },
  });

  const orderActionMutation = useMutation(
    (payload: { orderId: number; actionId: number }) =>
      SupplyManagmentService.updateOrderAction(payload),
    {
      onMutate: (variables) => {
        setActionRowId(variables.orderId);
      },
      onSuccess: (res: any, variables) => {
        const generatedBarcodes = extractBarcodeRecords(res?.data?.data);
        if (generatedBarcodes.length) {
          setPendingBarcodes((prev) => ({
            ...prev,
            [variables.orderId]: mergeBarcodeRecords(
              prev[variables.orderId] ?? [],
              generatedBarcodes
            ),
          }));
        }

        if (
          res?.data?.httpStatusCode === 200 ||
          res?.data?.status === "Success"
        ) {
          toast.success(res?.data?.message);
        } else if (res?.data?.message) {
          toast.error(res?.data?.message);
        }

        queryClient.invalidateQueries(oncologySupplyQueryKeys.ordersRoot());
        queryClient.invalidateQueries(
          oncologySupplyQueryKeys.order(variables.orderId)
        );
      },
      onError: (error: any) => {
        console.error(error);
        toast.error(
          error?.response?.data?.message ||
            t("Action failed. Please try again.")
        );
      },
      onSettled: () => {
        setActionRowId(null);
      },
    }
  );

  const printSupplyContentMutation = useMutation(
    ({ payload }: SupplyPrinterContentVariables) =>
      SupplyManagmentService.getSupplyPrinterContent(payload),
    {
      onMutate: (variables) => {
        setPrintBarcodeRowId(variables.payload.supplyOrderId);
      },
      onSuccess: async (res: any, variables) => {
        const printerContent = res?.data?.data;

        if (!Array.isArray(printerContent) || !printerContent.length) {
          toast.error(
            res?.data?.message || t("Unable to fetch printer content.")
          );
          return;
        }

        await printSupplyPrinterContent(printerContent, variables.printer);
      },
      onError: (error: any) => {
        console.error(error);
        toast.error(
          error?.response?.data?.message ||
            t("Unable to fetch printer content.")
        );
      },
      onSettled: () => {
        setPrintBarcodeRowId(null);
      },
    }
  );

  const base64ToExcel = (base64: string, filename: string) => {
    const decodedBase64 = atob(base64);
    const workbook = XLSX.read(decodedBase64, { type: "binary" });
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const excelBlob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(excelBlob, `${filename}.xlsx`);
  };

  const applySearch = () => {
    setCurPage(1);
    setAppliedSearch({
      ...searchRequest,
      status: mode === "new" ? "" : statusByMode[mode],
    });
  };

  const resetSearch = () => {
    const resetState = getInitialSearchState(mode);
    const isQueryAlreadyReset =
      curPage === 1 &&
      sort.clickedIconData === sortById.clickedIconData &&
      sort.sortingOrder === sortById.sortingOrder &&
      Object.entries(resetState).every(
        ([key, value]) =>
          appliedSearch[key as keyof typeof appliedSearch] === value
      );

    setSearchRequest(resetState);
    setAppliedSearch(resetState);
    setSorting(sortById);
    setCurPage(1);

    if (isQueryAlreadyReset) {
      refetchOrders();
    }
  };

  const handleSort = (columnName: string) => {
    setSorting((prev) => ({
      clickedIconData: columnName,
      sortingOrder:
        prev.clickedIconData === columnName && prev.sortingOrder === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const onInputChangeSearch = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setSearchRequest((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      applySearch();
    }
  };

  const handleAllSelect = (checked: boolean) => {
    setSelectedIds(checked ? rows.map((item) => item.id) : []);
  };

  const handleSelectRow = (checked: boolean, id: number) => {
    setSelectedIds((prev) =>
      checked ? [...new Set([...prev, id])] : prev.filter((item) => item !== id)
    );
  };

  const handleTagRemoval = (clickedTag: string) => {
    const nextSearch = {
      ...searchRequest,
      [clickedTag]: (initialSearchQuery as any)[clickedTag],
    };
    setSearchRequest(nextSearch);
    setCurPage(1);
    setAppliedSearch({
      ...nextSearch,
      status: mode === "new" ? "" : statusByMode[mode],
    });
  };

  const searchedTags = useMemo(() => {
    const uniqueKeys = new Set<string>();
    for (const [key, value] of Object.entries(searchRequest)) {
      if (value) uniqueKeys.add(key);
    }
    return Array.from(uniqueKeys);
  }, [searchRequest]);

  const handleExport = (selectedOnly: boolean) => {
    if (selectedOnly && selectedIds.length === 0) {
      toast.error(t("Select atleast one record"));
      return;
    }

    exportMutation.mutate(selectedOnly);
  };

  const handleAddNew = () => {
    navigate("/supply-order-oncology");
  };

  const openShipModal = () => {
    if (!selectedRows.length) {
      toast.error(t("Please select atleast one record"));
      return;
    }

    if (!canShipSelection) {
      toast.error(
        t("Only Approved or Barcode Generated orders can be shipped")
      );
      return;
    }

    const sameFacility = selectedRows.every(
      (item) => item.facilityName === selectedRows[0].facilityName
    );

    if (!sameFacility) {
      toast.error(t("Please select the same facility"));
      return;
    }

    setShowShipModal(true);
  };

  const shipNow = () => {
    if (shipMutation.isLoading) return;

    if (!selectedRows.length) {
      toast.error(t("Please select atleast one record"));
      return;
    }

    if (!canShipSelection) {
      toast.error(
        t("Only Approved or Barcode Generated orders can be shipped")
      );
      return;
    }

    if (!shippingNow.courierName || !shippingNow.trackingNumber) {
      toast.error(t("Please fill the required details"));
      return;
    }

    shipMutation.mutate({
      ids: selectedIds,
      courierName: shippingNow.courierName,
      trackingNumber: shippingNow.trackingNumber,
    });
  };

  const handleToggleExpand = (row: SupplyOrder) => {
    setExpandedOrderId((prev) => (prev === row.id ? null : row.id));
  };

  const handleOrderAction = (row: SupplyOrder, action: OrderAction) => {
    orderActionMutation.mutate({
      orderId: row.id,
      actionId: Number(action.value),
    });
  };

  const printBarcodes = async (records: BarcodeRecord[]) => {
    await printBarcodeRecords(records, selectedPrinter);
  };

  const getPrinterSelection = () => {
    const printerId = Number(selectedPrinter?.value);

    if (!selectedPrinter || !Number.isFinite(printerId) || printerId <= 0) {
      toast.error(t("Please select a printer"));
      return null;
    }

    return { printer: selectedPrinter, printerId };
  };

  const handlePrintOrderBarcodes = (row: SupplyOrder) => {
    const selection = getPrinterSelection();
    if (!selection) return;

    printSupplyContentMutation.mutate({
      payload: {
        printerId: selection.printerId,
        supplyOrderId: row.id,
        kitName: "",
        barcode: "",
      },
      printer: selection.printer,
    });
  };

  const handlePrintItemBarcodes = (
    order: SupplyOrder,
    item: SupplyOrderItem
  ) => {
    const selection = getPrinterSelection();
    if (!selection) return;

    const supplyOrderItemId = Number(item.id);
    if (!Number.isFinite(supplyOrderItemId) || supplyOrderItemId <= 0) {
      toast.error(t("Unable to identify the selected supply item"));
      return;
    }

    printSupplyContentMutation.mutate({
      payload: {
        printerId: selection.printerId,
        supplyOrderId: order.id,
        supplyItemId: supplyOrderItemId,
        kitName: "",
        barcode: "",
      },
      printer: selection.printer,
    });
  };

  const handlePrintBarcode = (record: BarcodeRecord) => {
    const selection = getPrinterSelection();
    if (!selection) return;

    printSupplyContentMutation.mutate({
      payload: {
        printerId: selection.printerId,
        supplyOrderId: null,
        kitName: record.itemName ?? "",
        barcode: record.value,
      },
      printer: selection.printer,
    });
  };

  return (
    <>
      <Modal
        show={showShipModal}
        onHide={() => setShowShipModal(false)}
        keyboard={false}
      >
        <Modal.Header closeButton className="m-0 py-3 px-6">
          <h4>{t("Shipping Detail")}</h4>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-5">
            <p className="text-primary fw-500">
              {t("Specimens selected for shipment")}
            </p>
          </div>
          <div className="border-bottom">
            <ul>
              <li>{selectedRows[0]?.facilityName}</li>
            </ul>
          </div>
          <div className="mt-5 d-flex gap-5">
            <div className="w-50">
              <label className="mb-2 fw-500 required">
                {t("Shipping Courier")}
              </label>
              <Select
                inputId="OncologyManageOrderModalShippingCourier"
                menuPortalTarget={document.body}
                styles={reactSelectSMStyle}
                options={shippingCourier}
                theme={(theme: any) => styles(theme)}
                placeholder={t("Shipping Courier")}
                name="courierName"
                value={shippingCourier.filter(
                  (item) => item.label === shippingNow.courierName
                )}
                onChange={(event: any) => {
                  setShippingNow((prev) => ({
                    ...prev,
                    courierName: event.value,
                  }));
                }}
              />
            </div>
            <div className="w-50">
              <label className="required mb-2 fw-500">{t("Tracking No")}</label>
              <input
                id="OncologyManageOrderModalTrackingNumber"
                type="text"
                name="trackingNumber"
                onChange={(e) =>
                  setShippingNow((prev) => ({
                    ...prev,
                    trackingNumber: e.target.value,
                  }))
                }
                className="form-control bg-white min-w-100px w-100 rounded-2 fs-8 h-33px"
                placeholder={t("Tracking Number")}
                value={shippingNow.trackingNumber}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="py-2 px-6">
          <button
            id="OncologyManageOrderModalCancel"
            type="button"
            className="badge badge-pill badge-danger py-3 px-4 border-0 fw-400 fa-1x text-light"
            onClick={() => setShowShipModal(false)}
          >
            {t("Cancel")}
          </button>
          <button
            id="OncologyManageOrderModalShipNow"
            type="button"
            className="badge badge-pill badge-primary py-3 px-4 border-0 fw-400 fa-1x text-light"
            onClick={shipNow}
          >
            {shipMutation.isLoading ? t("Shipping...") : t("Ship Now")}
          </button>
        </Modal.Footer>
      </Modal>

      <div className="d-flex gap-4 flex-wrap mb-2">
        {searchedTags.map((tag) =>
          tag === "isRejected" ||
          (tag === "status" && mode !== "new") ? null : (
            <div
              className="d-flex align-items-center cursor-pointer gap-1 p-2 rounded bg-light pe-1"
              onClick={() => handleTagRemoval(tag)}
              key={tag}
            >
              <span className="fw-bold">{t(queryDisplayTagNames[tag])}</span>
              <i className="bi bi-x"></i>
            </div>
          )
        )}
      </div>

      <div className="mb-2 d-flex flex-wrap gap-2 justify-content-center justify-content-md-between align-items-center">
        <div className="d-flex align-items-center gap-2 flex-wrap justify-content-center">
          <div className="d-flex align-items-center">
            <span className="fw-400 mr-3">{t("Records:")}</span>
            <select
              id={`OncologyManageOrder${mode}Records`}
              className="form-select w-125px h-33px rounded py-2"
              onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
              value={pageSize}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          {mode === "new" && (
            <PermissionComponent
              moduleName="Supply Management"
              pageName="Manage Order (Oncology)"
              permissionIdentifier="Add"
            >
              <button
                id="OncologyManageOrderAddNew"
                className="btn btn-primary btn-sm btn-primary--icon px-7"
                onClick={handleAddNew}
              >
                <span>
                  <i style={{ fontSize: "15px" }} className="fa">
                    &#xf067;
                  </i>
                  <span> {t("Add New")} </span>
                </span>
              </button>
            </PermissionComponent>
          )}
          {mode === "new" && (
            <PermissionComponent
              moduleName="Supply Management"
              pageName="Manage Order (Oncology)"
              permissionIdentifier="Ship"
            >
              <button
                id="OncologyManageOrderShip"
                className="badge badge-pill badge-warning fw-400 fa-1x h-33px px-6 text-light border-0"
                onClick={openShipModal}
                disabled={!canShipSelection || shipMutation.isLoading}
              >
                <span>{t("Ship")}</span>
              </button>
            </PermissionComponent>
          )}
          <PermissionComponent
            moduleName="Supply Management"
            pageName="Manage Order (Oncology)"
            permissionIdentifier="Export"
          >
            <>
              <StyledDropButton
                id={`OncologyManageOrder${mode}BulkExport`}
                aria-controls={
                  exportAnchorEl ? "oncology-export-menu" : undefined
                }
                aria-haspopup="true"
                aria-expanded={exportAnchorEl ? "true" : undefined}
                onClick={(event) => setExportAnchorEl(event.currentTarget)}
                className="btn btn-excle btn-sm"
              >
                <i
                  style={{
                    color: "white",
                    fontSize: "20px",
                    paddingLeft: "2px",
                  }}
                  className="fa"
                >
                  &#xf1c3;
                </i>
                <span className="svg-icon svg-icon-5 m-0">
                  <ArrowBottomIcon />
                </span>
              </StyledDropButton>
              <StyledDropMenu
                id={`OncologyManageOrder${mode}BulkExportMenu`}
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={() => setExportAnchorEl(null)}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                <MenuItem className="p-0">
                  <a
                    id={`OncologyManageOrder${mode}ExportAll`}
                    className="w-200px p-0 text-dark"
                    onClick={() => {
                      setExportAnchorEl(null);
                      handleExport(false);
                    }}
                  >
                    <i className="fa text-excle mr-2 w-20px">&#xf1c3;</i>
                    {t("Export All Records")}
                  </a>
                </MenuItem>
                <MenuItem className="p-0">
                  <a
                    id={`OncologyManageOrder${mode}ExportSelected`}
                    className="w-200px p-0 text-dark"
                    onClick={() => {
                      setExportAnchorEl(null);
                      handleExport(true);
                    }}
                  >
                    <i className="fa text-success mr-2 w-20px">&#xf15b;</i>
                    {t("Export Selected Records")}
                  </a>
                </MenuItem>
              </StyledDropMenu>
            </>
          </PermissionComponent>
        </div>

        <div className="d-flex align-items-center gap-2 gap-lg-3">
          <PermissionComponent
            moduleName="Supply Management"
            pageName="Manage Order (Oncology)"
            permissionIdentifier="Printer"
          >
            {printers.length > 0 && (
              <div className="min-w-175px">
                <Select
                  inputId={`OncologyManageOrder${mode}Printer`}
                  menuPortalTarget={document.body}
                  styles={reactSelectSMStyle}
                  options={printers}
                  theme={(theme: any) => styles(theme)}
                  placeholder={t("Printer")}
                  value={printers.filter(
                    (printer) => printer.value === selectedPrinter?.value
                  )}
                  onChange={(event: any) => setSelectedPrinter(event)}
                />
              </div>
            )}
          </PermissionComponent>
          <button
            id={`OncologyManageOrder${mode}Search`}
            onClick={applySearch}
            className="btn btn-linkedin btn-sm fw-500"
          >
            {t("Search")}
          </button>
          <button
            onClick={resetSearch}
            type="button"
            className="btn btn-secondary btn-sm btn-secondary--icon fw-500"
            id={`OncologyManageOrder${mode}Reset`}
          >
            <span>
              <span>{t("Reset")}</span>
            </span>
          </button>
        </div>
      </div>

      <div className="card">
        <Box sx={{ height: "auto", width: "100%" }}>
          <OrderListTable
            actionLookup={actionLookup}
            expandedOrderId={expandedOrderId}
            loading={loading}
            mode={mode}
            orderActionLoading={orderActionMutation.isLoading}
            orderActionRowId={actionRowId}
            pendingBarcodes={pendingBarcodes}
            printBarcodeLoading={printSupplyContentMutation.isLoading}
            printBarcodeRowId={printBarcodeRowId}
            rows={rows}
            searchRequest={searchRequest}
            selectedIds={selectedIds}
            selectAll={selectAll}
            sort={sort}
            onOrderAction={handleOrderAction}
            onPrintBarcode={handlePrintBarcode}
            onPrintBarcodes={printBarcodes}
            onPrintItemBarcodes={handlePrintItemBarcodes}
            onPrintOrderBarcodes={handlePrintOrderBarcodes}
            onSearchChange={onInputChangeSearch}
            onSearchKeyPress={handleSearchKeyPress}
            onSelectAll={handleAllSelect}
            onSelectRow={handleSelectRow}
            onSort={handleSort}
            onToggleExpand={handleToggleExpand}
          />
          <CustomPagination
            curPage={curPage}
            nextPage={nextPage}
            pageNumbers={pageNumbers}
            pageSize={pageSize}
            prevPage={prevPage}
            showPage={showPage}
            total={total}
            totalPages={totalPages}
          />
        </Box>
      </div>
    </>
  );
}

export default OrderList;
